import { useDispatch, useSelector } from 'react-redux';

// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { SecondaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { AppDispatch } from 'types/RootState.types';
import { POSCreditOptionsProps } from './POSCreditOptions.types';

// Config
import POSCreditOptionsState from './POSCreditOptions.state';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { creditTypes } from '@constants/posCreditConstants';
import { setSelectedPaymentType } from '@redux/actions/payment/paymentActions';
import { showEditBasketModal } from '@redux/actions/edit-basket/editBasketActions';
import { scrollToElement } from '@utils/index';
import getPosCreditOptionLabel from '@utils/payment/posCreditLabelMappings';

// Components
import PaymentOption from '@components/add-payment-details/payment-option';
import Container from '@components/container';
import POSCredit from '@components/pos-credit';

const POSCreditOptions = ({
  creditTypeGroups = [],
  disabled = false,
  showOrderTotals = false,
}: POSCreditOptionsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    pathname,
    posCreditBasketExcess,
    savedPaymentCards = [],
    selectedPaymentType,
  } = useSelector(POSCreditOptionsState);

  const posCreditOptionClickHandler = (paymentType: string) => {
    const selectedCreditTypeGroupId = paymentType.split(':')[1];
    const creditTypeGroup = creditTypeGroups.find(c => c.id === selectedCreditTypeGroupId);
    const posCreditType = creditTypeGroup?.paymentOptionMetaData?.optionType === creditTypes.INTEREST_FREE ? 'ifc' : 'ibc';
    const clickedPaymentOptionIsAlreadySelected = selectedPaymentType === paymentType;

    if (!clickedPaymentOptionIsAlreadySelected) {
      setTimeout(() => {
        dispatch(
          setSelectedPaymentType({
            paymentType,
            posCreditType,
            savedPaymentCards,
            shouldSetBillingAddress: true,
          }),
        );

        scrollToElement({ id: paymentType });
      }, 0);
    }
  };

  let posCreditOption;

  const basketExcessClickHandler = () => {
    dispatch(showEditBasketModal({ baseRoutePath: pathname, condition: 'excess' }));
  };

  if (creditTypeGroups.length > 0) {
    posCreditOption = (
      <div>
        {creditTypeGroups.map((group) => {

          const { paymentIcon } = group.paymentOptionMetaData ?? {};
          const isPaymentHoliday =
            group.paymentOptionMetaData?.optionType === paymentTypeConstants.POS_CREDIT_PAYMENT_HOLIDAY;
          let groupId = `${paymentTypeConstants.POS_CREDIT}:${group.id}`;
          let label = getPosCreditOptionLabel(group);

          if (isPaymentHoliday) {
            groupId = `${groupId}:deferred`;
            label = 'Buy now, pay later';
          }

          const expandedContent = () => (
            <Container borderTop="black" padding="2" testId="expanded-pay-later">
              <POSCredit
                creditTypeGroup={group}
                groupId={groupId}
                isPaymentHoliday={isPaymentHoliday}
                paymentButtonDisabled={disabled}
                showOrderTotals={!isPaymentHoliday && showOrderTotals}
              />
            </Container>
          );

          return (
            <PaymentOption
              key={group.id}
              data-testid={`payment-option-${paymentTypeConstants.POS_CREDIT}`}
              disabled={disabled}
              id={groupId}
              isPaymentHoliday={isPaymentHoliday}
              label={label}
              name="POSPaymentOption"
              onClick={posCreditOptionClickHandler}
              posCreditIconDetails={!isPaymentHoliday ? paymentIcon : undefined}
              selected={selectedPaymentType === groupId}
            >
              {expandedContent()}
            </PaymentOption>
          );
        })}
      </div>
    );
  } else if (posCreditBasketExcess) {
    posCreditOption = (
      <Container marginTop="1">
        <PaymentOption
          data-testid={`payment-option-${paymentTypeConstants.POS_CREDIT}`}
          disabled={disabled}
          id={`${paymentTypeConstants.POS_CREDIT}:posCreditBasketExcess`}
          label="Credit payment options"
          name="POSPaymentOption"
          onClick={posCreditOptionClickHandler}
          selected={selectedPaymentType === `${paymentTypeConstants.POS_CREDIT}:posCreditBasketExcess`}
        >
          <Container borderTop="black" padding="2">
            <Message
              body={
                <>
                  <Container marginBottom="2">
                    Some payment options are not available as there are too many items in your basket. Please remove{' '}
                    {`${posCreditBasketExcess} item${posCreditBasketExcess > 1 ? 's ' : ' '}`} to view all payment
                    options.
                  </Container>
                  <SecondaryButton className="mb-0" onClick={basketExcessClickHandler} size="small">
                    Edit basket
                  </SecondaryButton>
                </>
              }
              dataTestId="pos-credit-basket-amend-message"
              title="Credit not available"
              type="warning"
            />
          </Container>
        </PaymentOption>
      </Container>
    );
  }

  return posCreditOption ?? <></>;
};

export default POSCreditOptions;
